<template>
  <div class="register-container">
    <!-- 关闭按钮：固定在屏幕右上角 -->
    <div class="close-btn">
      <el-icon @click="handleClose" class="close-icon">
        <Close />
      </el-icon>
    </div>
    
    <!-- 注册表单方块：居中显示 -->
    <div class="register-box">
      <div class="logo">
        <img src="@/static/logo.png" alt="子沐研究" />
        <h2>注册账号</h2>
      </div>
      
      <!-- 注册表单 -->
      <el-form ref="registerFormRef" :model="form" :rules="rules" label-width="90px" :disabled="isLoading">
        
        <el-form-item label="用户名" prop="user_name">
          <el-input v-model="form.user_name" placeholder="请输入用户名" maxlength="20"/>
        </el-form-item>
        
        <el-form-item label="手机号" prop="tel">
          <el-input v-model="form.tel" placeholder="请输入手机号" maxlength="11" @input="formatTel"/>
        </el-form-item>
        
        <el-form-item label="密码" prop="passwd">
          <el-input v-model="form.passwd" type="password" placeholder="6-20位密码" show-password maxlength="20"/>
        </el-form-item>
        
        <el-form-item label="确认密码" prop="confirmPasswd">
          <el-input v-model="form.confirmPasswd" type="password" placeholder="请再次输入密码" show-password maxlength="20"/>
        </el-form-item>
                  
        <!-- 验证码 -->
        <el-form-item label="验证码" prop="captcha">
          <div class="captcha-box">
            <el-input v-model="form.captcha" placeholder="请输入验证码" maxlength="4"/>
            <SIdentify 
              :identifyCode="identifyCode" 
              @click="refreshCaptcha"
            />
          </div>
        </el-form-item>
        
        <!-- 用户协议 -->
        <el-form-item prop="agreeProtocol">
          <el-checkbox v-model="form.agreeProtocol">
            我已阅读并同意<span class="protocol-link" @click="showProtocol">《用户协议》</span>
          </el-checkbox>
        </el-form-item>
        
        <!-- 按钮组 -->
        <el-form-item class="button-group">
          <el-button type="primary" @click="handleRegister" class="register-btn" :loading="isLoading">
            注册
          </el-button>
          <el-button type="default" @click="backToLogin" class="back-btn">
            返回登录
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { userLoginApi } from '@/api/index';
import { useUserStore } from '@/store/userStore.js';
import SIdentify from '@/components/SIdentify.vue';

let ipcRenderer = null;
try {
  if (window.Electron && window.Electron.ipcRenderer) {
    ipcRenderer = window.Electron.ipcRenderer;
  }
} catch (e) {
  console.error('获取ipcRenderer失败：', e);
}

// Cookie操作工具函数
const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/`;
};

export default {
  components: {
    SIdentify
  },
  setup() {
    const router = useRouter();
    const registerFormRef = ref(null);
    const userStore = useUserStore();

    const form = reactive({
      user_name: '',
      tel: '',
      passwd: '',
      confirmPasswd: '',
      captcha: '',
      agreeProtocol: false,
    });

    const isLoading = ref(false);
    const identifyCode = ref('');

    // 生成随机验证码
    const makeCode = (length = 4) => {
      const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
      let code = '';
      for (let i = 0; i < length; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    };


    // 表单验证规则
    const validatePass = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请输入密码'));
      } else if (value.length < 6 || value.length > 20) {
        callback(new Error('密码长度需在6-20位之间'));
      } else {
        if (form.confirmPasswd !== '' && registerFormRef.value) {
          registerFormRef.value.validateField('confirmPasswd');
        }
        callback();
      }
    };

    const validatePass2 = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== form.passwd) {
        callback(new Error('两次输入密码不一致'));
      } else {
        callback();
      }
    };

    const validateProtocol = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请阅读并同意用户协议'));
      } else {
        callback();
      }
    };

    const validateCaptcha = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请输入验证码'));
      } else if (value.toLowerCase() !== identifyCode.value.toLowerCase()) {
        callback(new Error('验证码错误'));
      } else {
        callback();
      }
    };

    const rules = reactive({
      user_name: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 2, max: 20, message: '用户名长度需在2-20位之间', trigger: 'blur' }
      ],
      tel: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
      ],
      passwd: [
        { required: true, validator: validatePass, trigger: 'blur' }
      ],
      confirmPasswd: [
        { required: true, validator: validatePass2, trigger: 'blur' }
      ],
      captcha: [
        { required: true, validator: validateCaptcha, trigger: 'blur' }
      ],
      agreeProtocol: [
        { validator: validateProtocol, trigger: 'change' }
      ]
    });

    // 手机号格式化
    const formatTel = () => {
      form.tel = form.tel.replace(/\D/g, '');
    };

    // 刷新验证码
    const refreshCaptcha = () => {
      identifyCode.value = makeCode(4);
      form.captcha = '';
    };

    // 注册逻辑
    const handleRegister = async () => {
      // 表单验证
      const validateResult = await registerFormRef.value.validate().catch(err => {
        console.error('表单验证失败：', err);
        return false;
      });
      if (!validateResult) return;

      isLoading.value = true;
      try {
        // 调用注册接口（只提交手机号和密码，确认密码和验证码只用于前端验证）
        await userLoginApi.userRegister({
          user_name: form.user_name,
          tel: form.tel,
          passwd: form.passwd,
          head_url: '',
          user_desc:''
        });

        ElMessage.success('注册成功，正在自动登录...');

        // 注册成功后自动登录
        const loginData = await userLoginApi.userLogin({
          tel: form.tel,
          passwd: form.passwd
        });

        // 存储Token和用户信息
        setCookie('HTTP_USER_TOKEN', loginData.HTTP_USER_TOKEN, 30);
        localStorage.setItem('HTTP_USER_TOKEN', loginData.HTTP_USER_TOKEN);
        localStorage.setItem('userInfo', JSON.stringify({
          userId: loginData.user_id,
          tel: loginData.tel,
          invitationCode: loginData.invitation_code
        }));
        localStorage.setItem('isLoggedIn', 'true');

        userStore.loginSuccess({
          userId: loginData.user_id,
          token: loginData.HTTP_USER_TOKEN,
          tel: loginData.tel
        });

        // 调用频次记录接口
        try {
          await userLoginApi.userActive({
            user_id: loginData.user_id
          });
        } catch (err) {
          console.error('频次记录失败：', err);
        }

        // 调用权益激活接口
        try {
          await userLoginApi.userOrderTrial({
            user_id: loginData.user_id
          });
        } catch (err) {
          console.error('权益激活失败：', err);
          // 新注册用户如果已申请过体验,需要提示
          if (err.response?.data?.code === 10002) {
            ElMessageBox.alert(
              err.response.data.info || '您已经申请过体验了',
              '温馨提示',
              {
                confirmButtonText: '我知道了',
                type: 'info'
              }
            );
          }
        }

        // 窗口最大化
        if (ipcRenderer) {
          ipcRenderer.send('window-maximize');
          if (window.Electron.windowMaximize) {
            window.Electron.windowMaximize();
          }
        }

        ElMessage.success('登录成功');
        router.push('/home');
      } catch (error) {
        console.error('注册失败：', error);
        ElMessage.error(error.message || '注册失败，请稍后重试');
        // 刷新验证码
        refreshCaptcha();
      } finally {
        isLoading.value = false;
      }
    };

    // 返回登录
    const backToLogin = () => {
      router.push('/login');
    };

    // 显示用户协议
    const showProtocol = () => {
      ElMessageBox.alert(
        '这里是用户协议内容...\n（待补充具体协议内容）',
        '用户协议',
        {
          confirmButtonText: '我知道了'
        }
      );
    };

    // 关闭窗口
    const handleClose = () => {
      try {
        if (ipcRenderer) {
          ipcRenderer.send('close-main-window');
        } else {
          window.close();
        }
      } catch (e) {
        console.error('关闭窗口失败：', e);
        ElMessage.warning('关闭失败，请手动关闭应用');
      }
    };


    // 组件挂载时生成验证码
    onMounted(() => {
      refreshCaptcha();
    });

    return {
      form,
      rules,
      isLoading,
      registerFormRef,
      identifyCode,
      handleRegister,
      backToLogin,
      formatTel,
      refreshCaptcha,
      showProtocol,
      handleClose
    };
  }
};
</script>

<style scoped>
/* 全屏容器：铺满整个屏幕 */
.register-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, hsl(240, 7%, 91%) 0%, hwb(270 97% 2%) 100%);
  margin: 0;
  padding: 0;
  overflow: hidden;
}

/* 关闭按钮：固定在屏幕右上角 */
.close-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  cursor: pointer;
  z-index: 1000;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.close-btn:hover {
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.close-icon {
  font-size: 20px;
  color: #666;
  transition: color 0.2s;
}

.close-btn:hover .close-icon {
  color: #333;
}

/* 注册方块：居中的小卡片 */
.register-box {
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 40px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 1;
}

.logo {
  text-align: center;
  margin-bottom: 28px;
}

.logo img {
  height: 56px;
  margin-bottom: 8px;
}

.logo h2 {
  font-size: 18px;
  color: #1f2937;
  margin: 0;
}

/* 验证码容器 */
.captcha-box {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-box :deep(.s-canvas) {
  flex-shrink: 0;
}

.captcha-box :deep(.s-canvas canvas) {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.captcha-box :deep(.s-canvas canvas:hover) {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

/* 用户协议 */
.protocol-link {
  color: #409eff;
  cursor: pointer;
}

.protocol-link:hover {
  text-decoration: underline;
}

/* 按钮组 */
.button-group {
  display: flex;
  gap: 16px;
  margin-top: 12px;
}

.register-btn, .back-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
}

/* 适配小屏幕 */
@media (max-width: 768px) {
  .register-box {
    width: 90%;
    padding: 28px 24px;
  }
  
  .close-btn {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
  }
  
  .close-icon {
    font-size: 18px;
  }
}
</style>
